import React from 'react';
import { getWeatherIconUrl } from '../services/weatherService';
import './WeatherCard.css';

/**
 * WeatherCard Component
 * Demonstrates: Props usage, conditional rendering, dynamic data display
 * @param {Object} weatherData - Weather data from API
 */
const WeatherCard = ({ weatherData }) => {
  if (!weatherData) {
    return null;
  }

  const {
    name,
    sys,
    main,
    weather,
    wind,
    clouds,
    dt
  } = weatherData;

  const iconUrl = getWeatherIconUrl(weather[0].icon);
  const date = new Date(dt * 1000);
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="weather-card">
      <div className="weather-header">
        <div className="location-info">
          <h1 className="city-name">{name}, {sys.country}</h1>
          <p className="date-info">{dayName}</p>
          <p className="date-full">{formattedDate}</p>
        </div>
      </div>

      <div className="weather-main">
        <div className="temperature-section">
          <img
            src={iconUrl}
            alt={weather[0].description}
            className="weather-icon-large"
          />
          <div className="temperature-display">
            <h2 className="temperature">{Math.round(main.temp)}°C</h2>
            <p className="weather-description">{weather[0].main}</p>
            <p className="weather-description-detail">{weather[0].description}</p>
          </div>
        </div>

        <div className="weather-details-grid">
          <div className="detail-item">
            <div className="detail-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v10m0 0L8 8m4 4 4-4"/>
                <path d="M12 12v10"/>
              </svg>
            </div>
            <div className="detail-content">
              <p className="detail-label">Feels Like</p>
              <p className="detail-value">{Math.round(main.feels_like)}°C</p>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
              </svg>
            </div>
            <div className="detail-content">
              <p className="detail-label">Humidity</p>
              <p className="detail-value">{main.humidity}%</p>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/>
              </svg>
            </div>
            <div className="detail-content">
              <p className="detail-label">Wind Speed</p>
              <p className="detail-value">{wind.speed} m/s</p>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
            </div>
            <div className="detail-content">
              <p className="detail-label">Pressure</p>
              <p className="detail-value">{main.pressure} hPa</p>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            </div>
            <div className="detail-content">
              <p className="detail-label">Visibility</p>
              <p className="detail-value">{(weatherData.visibility / 1000).toFixed(1)} km</p>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
              </svg>
            </div>
            <div className="detail-content">
              <p className="detail-label">Cloudiness</p>
              <p className="detail-value">{clouds.all}%</p>
            </div>
          </div>
        </div>

        <div className="temp-range">
          <div className="temp-min">
            <span className="temp-label">Min</span>
            <span className="temp-value">{Math.round(main.temp_min)}°C</span>
          </div>
          <div className="temp-divider"></div>
          <div className="temp-max">
            <span className="temp-label">Max</span>
            <span className="temp-value">{Math.round(main.temp_max)}°C</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherCard;
